import * as Sentry from '@sentry/browser';
import { ApiClientError } from './api/client';
import { api } from './api/endpoints';

/**
 * Zentrale Fehlerberichte der Web-App an GlitchTip (Sentry-kompatibel).
 *
 * - Die Angaben (DSN, Umgebung, Version) liefert der Server über `GET /version`, ohne DSN bleibt alles aus.
 * - Erfasst werden: unbehandelte Fehler und abgelehnte Promises (global), Render-Fehler von React
 *   (`createRoot`-Handler in main.tsx) und alles, was über `reportError` gemeldet wird.
 * - Fehler vor dem Start (auch globale `error`/`unhandledrejection`) werden gepuffert und danach
 *   nachgereicht. Ist der Server beim Start nicht erreichbar (offline gestartete PWA), wird beim
 *   nächsten `online` erneut versucht.
 * - Datenschutz: keine Nutzerangaben, Fragmente (`#token=…`) und Abfrageparameter werden aus allen
 *   Adressen entfernt (CLAUDE.md Regel 10).
 */

const MAX_PENDING = 20;
let started = false;
let starting = false;
const pending: { error: unknown; context: Record<string, unknown> | undefined }[] = [];

/** Entfernt Fragment und Abfrageparameter (Einmal-Links tragen das Token im Fragment). */
export function stripSensitiveUrlParts(url: string): string {
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

/** Erwartete Fehler (Netz weg, 4xx wie „nicht angemeldet“) sind keine Programmfehler. */
export function isExpectedError(error: unknown): boolean {
  return error instanceof ApiClientError && error.status < 500;
}

export function sanitizeEvent<T extends Sentry.Event>(event: T): T {
  delete event.user;
  if (event.request !== undefined) {
    if (event.request.url !== undefined) {
      event.request.url = stripSensitiveUrlParts(event.request.url);
    }
    delete event.request.query_string;
    delete event.request.cookies;
    if (event.request.headers !== undefined) {
      // Referer kann ebenfalls ein Fragment oder Token tragen
      delete event.request.headers['Referer'];
    }
  }
  return event;
}

export function sanitizeBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb {
  const data = breadcrumb.data;
  if (data !== undefined) {
    for (const key of ['url', 'from', 'to']) {
      const value: unknown = data[key];
      if (typeof value === 'string') data[key] = stripSensitiveUrlParts(value);
    }
  }
  return breadcrumb;
}

/** Meldet einen Fehler. Vor dem Start gepuffert, ohne DSN folgenlos. Wirft nie. */
export function reportError(error: unknown, context?: Record<string, unknown>): void {
  if (isExpectedError(error)) return;
  if (!started) {
    if (pending.length < MAX_PENDING) pending.push({ error, context });
    return;
  }
  try {
    Sentry.captureException(error, context === undefined ? undefined : { extra: context });
  } catch {
    // Fehlerberichte dürfen die Oberfläche nie stören
  }
}

function bufferGlobalError(event: ErrorEvent): void {
  reportError(event.error ?? event.message);
}

function bufferRejection(event: PromiseRejectionEvent): void {
  reportError(event.reason);
}

function stopBuffering(): void {
  window.removeEventListener('error', bufferGlobalError);
  window.removeEventListener('unhandledrejection', bufferRejection);
}

/**
 * Holt die Angaben vom Server und startet die Berichte (einmal beim Laden der App). Bis dahin landen
 * globale Fehler im Puffer, danach übernimmt die `globalHandlersIntegration`.
 */
export async function initErrorReporting(): Promise<void> {
  if (started || starting) return;
  starting = true;
  window.addEventListener('error', bufferGlobalError);
  window.addEventListener('unhandledrejection', bufferRejection);
  let config: Awaited<ReturnType<typeof api.version>>['errorReporting'];
  try {
    config = (await api.version()).errorReporting;
  } catch {
    // Server nicht erreichbar: Puffer behalten und beim nächsten Netzwechsel erneut versuchen
    starting = false;
    window.addEventListener('online', () => void initErrorReporting(), { once: true });
    return;
  }
  starting = false;
  stopBuffering();
  if (config === null) {
    pending.length = 0;
    return;
  }
  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    ...(config.release === null ? {} : { release: config.release }),
    // Bewusst ohne Standardliste: keine Sitzungs-, Leistungs- oder Bildschirmaufzeichnung
    defaultIntegrations: false,
    integrations: [
      Sentry.globalHandlersIntegration(),
      Sentry.browserApiErrorsIntegration(),
      Sentry.linkedErrorsIntegration(),
      Sentry.dedupeIntegration(),
      Sentry.functionToStringIntegration(),
      Sentry.eventFiltersIntegration(),
      Sentry.httpContextIntegration(),
      Sentry.breadcrumbsIntegration(),
    ],
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
    },
    initialScope: { tags: { service: 'barso-web' } },
    beforeSend: (event, hint) =>
      isExpectedError(hint.originalException) ? null : sanitizeEvent(event),
    beforeBreadcrumb: (breadcrumb) => sanitizeBreadcrumb(breadcrumb),
  });
  started = true;
  for (const { error, context } of pending.splice(0)) reportError(error, context);
}
